(() => {
  const P = 'oddit-section-video-testimonials';
  const SECTION_SELECTOR = '.' + P;
  const mobile = window.matchMedia('(max-width: 989px)');

  function sanitizeClone(root) {
    root.setAttribute('aria-hidden', 'true');
    root.setAttribute('data-clone', '');
    for (const el of [root, ...root.querySelectorAll('*')]) {
      for (const cls of [...el.classList]) {
        if (cls.indexOf('oddit-reveal') === 0) el.classList.remove(cls);
      }
      el.style.removeProperty('--oddit-stagger-index');
      el.style.removeProperty('opacity');
      el.style.removeProperty('transform');
      el.removeAttribute('id');
      if (el.matches('a, button, input, select, textarea, [tabindex]')) {
        el.setAttribute('tabindex', '-1');
      }
    }
    return root;
  }

  function initSection(section) {
    if (section._odditCarousel) return;
    const track = section.querySelector('.' + P + '__track');
    const dots = [...section.querySelectorAll('.' + P + '__dot')];
    const reals = [...section.querySelectorAll('.' + P + '__card')];
    const N = reals.length;
    if (!track || N < 2) return;

    const ctrl = new AbortController();
    const opts = { signal: ctrl.signal };
    section._odditCarousel = ctrl;

    const first = reals[0];
    for (const card of reals) track.insertBefore(sanitizeClone(card.cloneNode(true)), first);
    for (const card of reals) track.appendChild(sanitizeClone(card.cloneNode(true)));
    const slides = [...track.querySelectorAll('.' + P + '__card')];

    let currentDom = N;
    let isAdjusting = false;
    let scrollRaf = null;
    let settleTimer = null;
    let resizeTimer = null;

    function anchor() {
      const pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
      return track.scrollLeft + pad;
    }

    function posOf(dom) {
      return slides[dom] ? slides[dom].offsetLeft - slides[0].offsetLeft : 0;
    }

    function nearestDom() {
      const a = anchor();
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i < slides.length; i++) {
        const dist = Math.abs(slides[i].offsetLeft - a);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      return best;
    }

    function logicalIndex(dom) {
      return (((dom - N) % N) + N) % N;
    }

    function nearestInstance(logical) {
      let best = N + logical;
      let bestDist = Infinity;
      for (const dom of [logical, N + logical, 2 * N + logical]) {
        const dist = Math.abs(dom - currentDom);
        if (dist < bestDist) {
          bestDist = dist;
          best = dom;
        }
      }
      return best;
    }

    function updateActiveDot() {
      const active = logicalIndex(currentDom);
      dots.forEach((dot, i) => {
        dot.classList.toggle(P + '__dot--active', i === active);
      });
    }

    function setScrollInstant(pos) {
      isAdjusting = true;
      const snap = track.style.scrollSnapType;
      const behavior = track.style.scrollBehavior;
      track.style.scrollSnapType = 'none';
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = pos;
      requestAnimationFrame(() => {
        track.style.scrollSnapType = snap;
        track.style.scrollBehavior = behavior;
        requestAnimationFrame(() => {
          isAdjusting = false;
        });
      });
    }

    function normalize() {
      if (isAdjusting || track.classList.contains('is-dragging') || !mobile.matches) {
        updateActiveDot();
        return;
      }
      const nearest = nearestDom();
      currentDom = nearest;
      let normalized = nearest;
      if (nearest < N) normalized = nearest + N;
      else if (nearest >= 2 * N) normalized = nearest - N;
      if (normalized !== nearest) {
        currentDom = normalized;
        setScrollInstant(posOf(normalized));
      }
      updateActiveDot();
    }

    function scrollToDom(dom) {
      currentDom = dom;
      updateActiveDot();
      track.scrollTo({ left: posOf(dom), behavior: 'smooth' });
    }

    track.addEventListener(
      'scroll',
      () => {
        if (isAdjusting) return;
        if (!scrollRaf) {
          scrollRaf = requestAnimationFrame(() => {
            scrollRaf = null;
            if (isAdjusting) return;
            currentDom = nearestDom();
            updateActiveDot();
          });
        }
        if (!('onscrollend' in window)) {
          clearTimeout(settleTimer);
          settleTimer = setTimeout(normalize, 140);
        }
      },
      { passive: true, signal: ctrl.signal },
    );

    if ('onscrollend' in window) {
      track.addEventListener('scrollend', normalize, opts);
    }

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => scrollToDom(nearestInstance(i)), opts);
    });

    window.addEventListener(
      'resize',
      () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (mobile.matches) setScrollInstant(posOf(currentDom));
          updateActiveDot();
        }, 150);
      },
      opts,
    );

    updateActiveDot();
    if (mobile.matches) {
      setScrollInstant(posOf(currentDom));
      requestAnimationFrame(() => setScrollInstant(posOf(currentDom)));
    }

    section._odditTeardown = () => {
      ctrl.abort();
      clearTimeout(settleTimer);
      clearTimeout(resizeTimer);
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      section._odditCarousel = null;
    };
  }

  // Muted 5s preview on hover, plus click-to-play with sound. Runs after
  // initSection so the carousel clones are wired up too — a clone is the copy
  // actually on screen half the time. Touch devices get the click path only.
  const PREVIEW_MS = 5000;

  function initPreview(section) {
    if (section._odditPreview) return;
    const videos = [...section.querySelectorAll('video[data-video-index]')];
    if (!videos.length) return;

    const ctrl = new AbortController();
    const opts = { signal: ctrl.signal };
    let timer = null;
    let hovered = null;
    let manual = null;

    function reset(video) {
      video.pause();
      video.controls = false;
      video.muted = true;
      video.load(); // restores the poster frame
      const card = video.closest('.' + P + '__card');
      if (card) card.classList.remove('is-playing');
    }

    function play(video) {
      const promise = video.play();
      if (promise) promise.catch(() => {});
      return promise;
    }

    // Rewind rather than load() — load() drops the buffer, so the next hover
    // has nothing decoded yet and silently plays nothing.
    function stopPreview(video) {
      if (!video) return;
      video.pause();
      video.currentTime = 0;
    }

    // Drop out of click-to-play. restorePoster only when we're done with that
    // card — load() would dump the buffer we're about to preview from.
    function clearManual(restorePoster) {
      if (!manual) return;
      const video = manual;
      manual = null;
      video.pause();
      video.controls = false;
      video.muted = true;
      const card = video.closest('.' + P + '__card');
      if (card) card.classList.remove('is-playing');
      if (restorePoster) video.load();
      else video.currentTime = 0;
    }

    const canHover =
      window.matchMedia('(hover: hover)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    for (const video of videos) {
      video.muted = true;
      const card = video.closest('.' + P + '__card');
      if (!card || !canHover) continue;

      card.addEventListener(
        'pointerenter',
        () => {
          // Only a manual video that's still running blocks the preview —
          // once it's paused or ended, hovering takes over again.
          if (manual === video && !manual.paused && !manual.ended) return;
          clearManual(manual !== video);
          clearTimeout(timer);
          if (hovered && hovered !== video) stopPreview(hovered);
          hovered = video;
          video.currentTime = 0;
          const promise = play(video);
          // Count the 5s from first frame, not from pointer-enter: with
          // preload="metadata" a cold video spends a beat buffering, and the
          // window would otherwise expire before anything showed.
          if (!promise) {
            timer = setTimeout(() => stopPreview(video), PREVIEW_MS);
            return;
          }
          promise
            .then(() => {
              if (hovered !== video) {
                video.pause(); // pointer already left
                return;
              }
              timer = setTimeout(() => stopPreview(video), PREVIEW_MS);
            })
            .catch(() => {});
        },
        opts,
      );

      card.addEventListener(
        'pointerleave',
        () => {
          if (manual === video) return;
          clearTimeout(timer);
          stopPreview(video);
          if (hovered === video) hovered = null;
        },
        opts,
      );
    }

    section.addEventListener(
      'click',
      (e) => {
        const card = e.target.closest('.' + P + '__card');
        const video = card && card.querySelector('video[data-video-index]');
        if (!video) return;
        // Already playing intentionally — let the native controls handle the click.
        if (video.controls && !e.target.closest('.' + P + '__play')) return;
        clearTimeout(timer);
        hovered = null;
        videos.forEach((other) => {
          if (other !== video) reset(other);
        });
        manual = video;
        video.controls = true;
        video.muted = false;
        video.currentTime = 0;
        card.classList.add('is-playing');
        play(video);
      },
      opts,
    );

    section.addEventListener(
      'ended',
      (e) => {
        if (e.target !== manual) return;
        clearManual(true);
      },
      { capture: true, signal: ctrl.signal },
    );

    section._odditPreview = () => {
      clearTimeout(timer);
      ctrl.abort();
      section._odditPreview = null;
    };
  }

  function init(section) {
    initSection(section);
    initPreview(section);
  }

  document.querySelectorAll(SECTION_SELECTOR).forEach(init);

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector(SECTION_SELECTOR);
    if (section) init(section);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector(SECTION_SELECTOR);
    if (!section) return;
    if (section._odditTeardown) section._odditTeardown();
    if (section._odditPreview) section._odditPreview();
  });
})();
